import { useEffect } from 'react';
import { useGetAppSettingsQuery, useGetDocumentSettingsQuery } from '@SERVICES';
import { darkenColor, updatePrimaryButtonTextColor } from '@HELPERS/colorUtils';
import useAuth from '@HOOKS/useAuth';

export const useAppSettingsStyles = () => {
    const { state: { user } } = useAuth();
    
    // Force refetch when user logs in by including user as a dependency
    const { data: appSettings, refetch: refetchAppSettings } = useGetAppSettingsQuery(undefined, {
        // Skip the query if there's no user
        skip: !user,
    });
    
    const { data: documentSettings, refetch: refetchDocumentSettings } = useGetDocumentSettingsQuery(undefined, {
        // Skip the query if there's no user
        skip: !user,
    });

    // Refetch settings when user logs in
    useEffect(() => {
        if (user) {
            // Small delay to ensure auth token is properly set
            setTimeout(() => {
                refetchAppSettings();
                refetchDocumentSettings();
            }, 100);
        }
    }, [user, refetchAppSettings, refetchDocumentSettings]);

    useEffect(() => {
        if (typeof document !== 'undefined' && appSettings && documentSettings) {
            const root = document.documentElement;

            // Set APP-LEVEL colors (used in web interface)
            if (appSettings.primary_color) {
                root.style.setProperty('--marriott-primary', appSettings.primary_color);
                // Calculate a darker version for the subheader (30% darker)
                const darkerPrimary = darkenColor(appSettings.primary_color, 0.3);
                root.style.setProperty('--marriott-dark-blue', darkerPrimary);
                
                // Update automatic button text color based on primary color brightness
                updatePrimaryButtonTextColor(appSettings.primary_color);
            }

            if (appSettings.secondary_color) {
                root.style.setProperty('--app-secondary-color', appSettings.secondary_color);
            }

            if (appSettings.text_color) {
                root.style.setProperty('--app-text-color', appSettings.text_color);
            }

            if (appSettings.admin_accent_color) {
                root.style.setProperty('--admin-accent-color', appSettings.admin_accent_color);
            }

            // Set DOCUMENT-LEVEL colors (used only in PDF generation)
            if (documentSettings.vendor_email_color) {
                root.style.setProperty('--vendor-email-color', documentSettings.vendor_email_color);
            }

            if (documentSettings.vendor_section_color) {
                root.style.setProperty('--vendor-section-color', documentSettings.vendor_section_color);
            }

            if (documentSettings.header_text_color) {
                root.style.setProperty('--header-text-color', documentSettings.header_text_color);
            }

            if (documentSettings.footer_text_color) {
                root.style.setProperty('--footer-text-color', documentSettings.footer_text_color);
            }

            if (documentSettings.confidentiality_note_color) {
                root.style.setProperty('--confidentiality-note-color', documentSettings.confidentiality_note_color);
            }

            if (documentSettings.document_param_text_color) {
                root.style.setProperty('--document-param-text-color', documentSettings.document_param_text_color);
            }

            if (documentSettings.document_release_text_color) {
                root.style.setProperty('--document-release-text-color', documentSettings.document_release_text_color);
            }

            if (documentSettings.general_title_color) {
                root.style.setProperty('--general-title-color', documentSettings.general_title_color);
            }

            if (documentSettings.toc_chapter_title_color) {
                root.style.setProperty('--toc-chapter-title-color', documentSettings.toc_chapter_title_color);
            }

            if (documentSettings.toc_section_title_color) {
                root.style.setProperty('--toc-section-title-color', documentSettings.toc_section_title_color);
            }

            if (documentSettings.chapter_cover_page_color) {
                root.style.setProperty('--chapter-cover-page-color', documentSettings.chapter_cover_page_color);
            }
        }
    }, [appSettings, documentSettings]);

    return { appSettings, documentSettings };
};
